import { jsPDF } from 'jspdf';
import { calculatePageDimensions } from './dimensions';
import { setupPDFFonts, verifyFontEmbedding } from '../../utils/pdfFonts';
import { renderSpreadToPDF } from './spreadRenderer.jsx';
import { ensureFontEmbedding } from './pageRenderer';

/**
 * Generate a cover PDF for the book
 * @param {Object} book - Book data
 * @param {Object} options - Options for PDF generation
 * @returns {Promise<Blob>} - PDF blob
 */
export async function generateCoverPDF(book, options = {}) {
  try {
    console.log('Generating cover PDF from the left page of spread-0');
    
    if (!book || !book.spreads) {
      throw new Error('Book or spreads data is missing');
    }
    
    // Get first spread (spread-0)
    const firstSpread = book.spreads['spread-0'];
    
    if (!firstSpread) {
      throw new Error('First spread (spread-0) is missing');
    }
    
    console.log('Found first spread:', firstSpread.id);
    
    let width, height, scale;
    const isForLuluPrinting = options.forLuluPrinting || false;
    const bindingType = options.bindingType || 'perfect-bound';
    if (isForLuluPrinting) {
      const pageDims = calculatePageDimensions(book.pageSize);
      if (bindingType === 'perfect-bound') {
        width = 12.382 * 72;
        height = 9.25 * 72;
        scale = 1;
      } else if (bindingType === 'case-wrap' || bindingType === 'linen-wrap') {
        width = 14.0 * 72;
        height = 10.75 * 72;
        scale = 1;
      } else {
        width = 12.382 * 72;
        height = 9.25 * 72;
        scale = 1;
      }
      console.log('📏 Using Lulu-compatible cover dimensions:', `${width}pt x ${height}pt (${width/72}" x ${height/72}")`, bindingType);
    } else {
      const dims = calculatePageDimensions(book.pageSize);
      width = dims.width * 2.1;
      height = dims.height * 1.05;
      scale = dims.scale;
      console.log('Using preview dimensions:', `${width}pt x ${height}pt (${width/72}" x ${height/72}")`, bindingType);
    }
    
    // Create a new PDF in landscape format with EXACT dimensions
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'pt',
      format: [width, height],
      compress: true,  // Enable compression for smaller file size
      hotfixes: ['px_scaling'] // Disable any automatic scaling that might affect dimensions
    });
    
    // Log the actual document size to verify
    console.log(`PDF created with dimensions: ${doc.internal.pageSize.getWidth()}pt x ${doc.internal.pageSize.getHeight()}pt`);
    console.log(`In inches: ${doc.internal.pageSize.getWidth()/72}" x ${doc.internal.pageSize.getHeight()/72}"`);
    
    // Setup fonts with proper embedding
    console.log('Setting up fonts for cover PDF');
    const fontsSetupResult = await setupPDFFonts(doc);
    
    if (!fontsSetupResult) {
      throw new Error('Font setup failed for cover PDF');
    }
    
    // Get cover color
    const coverColor = 
      book?.coverDetails?.frontCover?.color || 
      book?.frontCoverColor || 
      '#FFFFFF';
    
    // Convert hex color to RGB for background
    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : { r: 255, g: 255, b: 255 };
    };
    
    const rgb = hexToRgb(coverColor);
    
    // Set background color for the entire spread
    // Make sure to fill the entire document area
    doc.setFillColor(rgb.r, rgb.g, rgb.b);
    doc.rect(0, 0, width, height, 'F');
    
    // No guidelines or barcode area to match the design view exactly

    // Pass proper orientation information based on dimensions
    const isLandscape = width > height;
    const orientation = isLandscape ? 'landscape' : 'portrait';
    
    // Using the exact same approach as horizontalPDF.js which works correctly
    // We need consistent scale and dimensions between design view and PDF
    
    // The design view uses COVER_DIMENSIONS (600 width, 800 height)
    // This matches how the double page view works
    const designWidth = 1200; // 600pt * 2 covers (front and back)
    const designHeight = 800; // Standard height in design view
    
    // Calculate a SINGLE consistent scale factor - crucial fix
    // Using different X and Y scales was causing distortion
    const uniformScale = Math.min(width / designWidth, height / designHeight);
    
    console.log('Using uniform scale factor for consistent rendering:', {
      designDimensions: { width: designWidth, height: designHeight },
      pdfDimensions: { width, height },
      uniformScale: uniformScale
    });
    
    // Pass orientation and proper scaling to the spread renderer for consistent positioning
    await renderSpreadToPDF(doc, firstSpread, { 
      scale: uniformScale, 
      scaleY: uniformScale,  // Use same scale for both dimensions to preserve aspect ratio
      isCoverSpread: true,  // This is CRITICAL - must be true for cover spreads to match design view
      offsetX: 0,
      // CRITICAL: Set this to false for vertical positioning to match design view
      flippedY: false, 
      orientation: orientation,
      pageSize: book.pageSize || 'us-trade',
      width: width,
      height: height,
      // No debug mode, we just want a clean PDF
      debug: false
    });
    
    console.log('Cover PDF generated successfully using spread-0');
    
    // Set PDF properties
    doc.setProperties({
      title: book?.title || 'Book Cover', 
      subject: 'Book Cover',
      creator: 'StorybookWiz',
      author: book?.author || 'Author'
    });

    // Verify font embedding
    verifyFontEmbedding(doc);
    
    // Return the PDF as a blob - using a standard method
    return new Blob([doc.output('arraybuffer')], { type: 'application/pdf' });
  } catch (error) {
    console.error('Error generating cover PDF:', error);
    return generateSimpleCover(book, options);
  }
}

/**
 * Generate a simple cover PDF as a fallback
 * @param {Object} book - The book object
 * @param {Object} options - Optional parameters
 * @returns {Promise<Blob>} - PDF as a blob
 */
export async function generateSimpleCover(book, options = {}) {
  try {
    console.log('Generating simple cover PDF');
    
    const isForLuluPrinting = options.forLuluPrinting || false;
    
    let width, height;
    
    if (isForLuluPrinting) {
      // HARDCODED: For Lulu 6x9 books (US Trade), exact dimensions for cover
      width = 12.382 * 72;  // 891.5pt - exact per Lulu specs
      height = 9.25 * 72;   // 666pt - exact per Lulu specs
      console.log('📏 Using EXACT Lulu cover dimensions for fallback cover:', 
                 `${width}pt x ${height}pt (${width/72}" x ${height/72}")`);
    } else {
      // Use standard dimensions with extra space for margins/bleed
      const dims = calculatePageDimensions(book.pageSize);
      width = dims.width * 2.1;  // Double width plus 10% for spread
      height = dims.height * 1.05; // Add 5% height
    }
    
    // Create PDF with specific dimensions
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'pt',
      format: [width, height],
      compress: true
    });
    
    // Setup fonts and ensure embedding
    await setupPDFFonts(doc);
    
    // Get cover color or use default white
    const coverColor = book?.coverDetails?.frontCover?.color || 
                      book?.frontCoverColor || 
                      '#FFFFFF';
    
    // Fill background with cover color
    doc.setFillColor(coverColor);
    doc.rect(0, 0, width, height, 'F');
    
    // Add book title
    const title = book?.title || 'Book Title';
    const author = book?.author || 'Author Name';
    
    // Set title font and size
    doc.setFont('Baskervville-Regular', 'normal');
    doc.setFontSize(36);
    
    // Add title (centered with proper margin)
    doc.setTextColor(0, 0, 0);
    const titleWidth = doc.getStringUnitWidth(title) * 36 / doc.internal.scaleFactor;
    const titleX = (width - titleWidth) / 2;
    doc.text(title, titleX, height / 2 - 40);
    
    // Add author (centered with proper margin)
    doc.setFontSize(24);
    const authorWidth = doc.getStringUnitWidth(author) * 24 / doc.internal.scaleFactor;
    const authorX = (width - authorWidth) / 2;
    doc.text(author, authorX, height / 2 + 20);
    
    // Set PDF properties
    doc.setProperties({
      title: book?.title || 'Book Cover',
      subject: 'Book Cover', 
      creator: 'StorybookWiz',
      author: book?.author || 'Author'
    });
    
    // Verify font embedding
    verifyFontEmbedding(doc);
    
    // Return the PDF as a blob
    return new Blob([doc.output('arraybuffer')], { type: 'application/pdf' });
  } catch (error) {
    console.error('Error generating simple cover:', error);
    throw error;
  }
}
